import React, { useEffect, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, makeStyles
} from '@material-ui/core'
import api from '../../api/client'
import { exportExcel } from '../../assets/excelUtils'
import { gregorianToJalali, toFarsiDigits } from '../../assets/utils'

const useStyles = makeStyles(() => ({
  wrap: { overflowX: 'auto', border: '1px solid #e3e6f2', borderRadius: 10 },
  headCell: {
    background: '#1a237e', color: '#fff', fontWeight: 700, fontSize: 11.5,
    whiteSpace: 'nowrap', padding: '8px 10px',
  },
  cell: { fontSize: 12, padding: '8px 10px', verticalAlign: 'top' },
  msgCell: { fontSize: 12, padding: '8px 10px', whiteSpace: 'pre-wrap', minWidth: 220 },
  unread: { color: '#c62828', fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap' },
  read: { color: '#2e7d32', fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap' },
}))

/**
 * گزارشِ پیام‌های مبادله‌شده در یک قرارداد:
 * تاریخ · ساعت · فرستنده · گیرنده · متن پیام · تاریخ و ساعتِ رویتِ گیرنده
 */
export default function ContractMessagesDialog({ open, onClose, contractId, contractNumber }) {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!open || !contractId) return undefined
    let alive = true
    setLoading(true)
    setErr('')
    api.get(`messages/contract/${contractId}/`)
      .then((res) => { if (alive) setData(res.data) })
      .catch((e) => {
        if (alive) setErr((e && e.response && e.response.data && e.response.data.detail) || 'خطا در دریافت پیام‌ها')
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [open, contractId])

  const exportXls = () => {
    if (!data) return
    const rows = (data.messages || []).map((m) => [
      toFarsiDigits(gregorianToJalali(m.date)),
      toFarsiDigits(m.time),
      toFarsiDigits(m.sender_name),
      toFarsiDigits(m.receiver_name),
      m.message,
      m.read_datetime
        ? `${toFarsiDigits(gregorianToJalali(m.read_date))} ${toFarsiDigits(m.read_time)}`
        : 'رویت نشده',
    ])
    exportExcel({
      filename: `contract-messages-${data.contract?.number || ''}.xlsx`,
      title: `گزارش پیام‌های قرارداد ${data.contract?.number || ''} — ${data.contract?.organization || ''}`,
      headers: ['تاریخ', 'ساعت', 'فرستنده', 'گیرنده', 'متن پیام', 'تاریخ و ساعت رویت گیرنده'],
      rows,
    })
  }

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" fullWidth style={{ direction: 'rtl' }}>
      <DialogTitle style={{ fontWeight: 800, color: '#1a237e' }}>
        گزارش پیام‌های قرارداد {toFarsiDigits(contractNumber || (data?.contract?.number) || '')}
        {data?.contract?.organization ? ` — ${toFarsiDigits(data.contract.organization)}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box textAlign="center" style={{ padding: 20 }}><CircularProgress /></Box>}
        {err && <Typography color="error">{err}</Typography>}

        {!loading && data && (
          <>
            <Typography variant="body2" style={{ color: '#5a6b8c', marginBottom: 8 }}>
              {toFarsiDigits(data.count || 0)} پیام در این قرارداد
            </Typography>
            <TableContainer className={classes.wrap} component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} align="center">تاریخ</TableCell>
                    <TableCell className={classes.headCell} align="center">ساعت</TableCell>
                    <TableCell className={classes.headCell} align="center">فرستنده</TableCell>
                    <TableCell className={classes.headCell} align="center">گیرنده</TableCell>
                    <TableCell className={classes.headCell} align="center">متن پیام</TableCell>
                    <TableCell className={classes.headCell} align="center">تاریخ و ساعت رویت گیرنده</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.messages || []).map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className={classes.cell} align="center">
                        {toFarsiDigits(gregorianToJalali(m.date))}
                      </TableCell>
                      <TableCell className={classes.cell} align="center">{toFarsiDigits(m.time)}</TableCell>
                      <TableCell className={classes.cell} align="center">{toFarsiDigits(m.sender_name)}</TableCell>
                      <TableCell className={classes.cell} align="center">{toFarsiDigits(m.receiver_name)}</TableCell>
                      <TableCell className={classes.msgCell}>{toFarsiDigits(m.message)}</TableCell>
                      <TableCell className={classes.cell} align="center">
                        {m.read_datetime ? (
                          <span className={classes.read}>
                            {toFarsiDigits(gregorianToJalali(m.read_date))} — {toFarsiDigits(m.read_time)}
                          </span>
                        ) : (
                          <span className={classes.unread}>رویت نشده</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!(data.messages || []).length && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography>پیامی برای این قرارداد ثبت نشده است.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={exportXls} color="default" variant="outlined" disabled={!data || !(data.messages || []).length}>
          دانلود اکسل
        </Button>
        <Button onClick={onClose} color="primary" variant="contained" style={{ fontWeight: 'bold' }}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  )
}
